import { useNavigate } from "react-router-dom";
import { ShoppingBag, Plus, Minus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart, euro } from "@/context/CartContext";

export const CartDrawer = () => {
  const { items, count, subtotal, deliveryFee, serviceFee, total, updateQty, removeItem } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button className="relative w-10 h-10 flex items-center justify-center text-neutral-900 hover:text-[#d00000] transition-colors" data-testid="cart-trigger" aria-label="Cart">
          <ShoppingBag className="w-5 h-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#d00000] text-white text-[11px] font-bold flex items-center justify-center rounded-full" data-testid="cart-count">
              {count}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-white border-l border-neutral-200 w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="p-6 border-b border-neutral-200">
          <SheetTitle className="font-display text-2xl font-black text-neutral-900 text-left">Your cart {count > 0 && `(${count})`}</SheetTitle>
          <SheetDescription className="sr-only">Review the dishes in your cart and proceed to checkout.</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-6" data-testid="cart-empty">
            <ShoppingBag className="w-12 h-12 text-neutral-300 mb-4" />
            <p className="font-display text-xl text-neutral-900">Your cart is empty</p>
            <p className="text-sm text-neutral-500 mt-2">Add some authentic dishes to get started.</p>
            <SheetClose asChild>
              <Button onClick={() => navigate("/marketplace")} className="mt-6 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">Browse meals</Button>
            </SheetClose>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {items.map((i) => (
                <div key={i.id} className="flex gap-3 border-b border-neutral-100 pb-4" data-testid={`cart-item-${i.id}`}>
                  <img src={i.img} alt={i.name} className="w-16 h-16 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-neutral-900 font-medium text-sm leading-tight">{i.name}</p>
                    <p className="text-xs text-neutral-400 mb-2">{i.cuisine}</p>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQty(i.id, i.qty - 1)} data-testid={`cart-dec-${i.id}`} className="w-7 h-7 border border-neutral-300 flex items-center justify-center hover:border-[#d00000]"><Minus className="w-3 h-3" /></button>
                      <span className="w-6 text-center text-sm text-neutral-900" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                      <button onClick={() => updateQty(i.id, i.qty + 1)} data-testid={`cart-inc-${i.id}`} className="w-7 h-7 border border-neutral-300 flex items-center justify-center hover:border-[#d00000]"><Plus className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end justify-between">
                    <span className="text-neutral-900 font-semibold text-sm">{euro(i.price * i.qty)}</span>
                    <button onClick={() => removeItem(i.id)} data-testid={`cart-remove-${i.id}`} className="text-neutral-400 hover:text-[#d00000]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-neutral-200 p-6 space-y-3">
              <div className="flex justify-between text-sm text-neutral-600"><span>Subtotal</span><span data-testid="cart-subtotal">{euro(subtotal)}</span></div>
              <div className="flex justify-between text-sm text-neutral-600"><span>Delivery</span><span>{deliveryFee ? euro(deliveryFee) : "Free"}</span></div>
              {serviceFee > 0 && <div className="flex justify-between text-sm text-neutral-600"><span>Service fee</span><span>{euro(serviceFee)}</span></div>}
              <div className="flex justify-between text-lg font-bold text-neutral-900"><span>Total</span><span data-testid="cart-total">{euro(total)}</span></div>
              <SheetClose asChild>
                <Button onClick={() => navigate("/checkout")} data-testid="cart-checkout-btn" className="w-full h-12 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none mt-2">
                  Checkout · {euro(total)}
                </Button>
              </SheetClose>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
